import { PointerEvent, useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import './SignaturePad.scss';

interface Props {
	onChange: (blob: Blob | null) => void;
	disabled?: boolean;
}

export interface SignaturePadHandle {
	clear: () => void;
}

function paintBackground(context: CanvasRenderingContext2D, canvas: HTMLCanvasElement) {
	context.fillStyle = '#ffffff';
	context.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
	context.save();
	context.strokeStyle = '#c9c9c9';
	context.lineWidth = 1;
	context.beginPath();
	context.moveTo(24, canvas.offsetHeight - 24);
	context.lineTo(canvas.offsetWidth - 24, canvas.offsetHeight - 24);
	context.stroke();
	context.restore();
}

const SignaturePad = forwardRef<SignaturePadHandle, Props>(({ onChange, disabled }, ref) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const drawing = useRef(false);
	const [hasStroke, setHasStroke] = useState(false);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}
		const resize = () => {
			const ratio = window.devicePixelRatio || 1;
			const width = canvas.offsetWidth * ratio;
			const height = canvas.offsetHeight * ratio;
			if (canvas.width === width && canvas.height === height) {
				return;
			}
			canvas.width = width;
			canvas.height = height;
			const context = canvas.getContext('2d');
			if (context) {
				context.scale(ratio, ratio);
				paintBackground(context, canvas);
				context.lineWidth = 2;
				context.lineCap = 'round';
				context.lineJoin = 'round';
				context.strokeStyle = '#111111';
			}
			setHasStroke(false);
			onChange(null);
		};
		resize();
		window.addEventListener('resize', resize);
		return () => window.removeEventListener('resize', resize);
	}, []);

	function positionOf(event: PointerEvent<HTMLCanvasElement>) {
		const rect = event.currentTarget.getBoundingClientRect();
		return { x: event.clientX - rect.left, y: event.clientY - rect.top };
	}

	function start(event: PointerEvent<HTMLCanvasElement>) {
		if (disabled) {
			return;
		}
		const context = canvasRef.current?.getContext('2d');
		if (!context) {
			return;
		}
		event.currentTarget.setPointerCapture(event.pointerId);
		drawing.current = true;
		const { x, y } = positionOf(event);
		context.beginPath();
		context.moveTo(x, y);
	}

	function move(event: PointerEvent<HTMLCanvasElement>) {
		if (!drawing.current) {
			return;
		}
		const context = canvasRef.current?.getContext('2d');
		if (!context) {
			return;
		}
		const { x, y } = positionOf(event);
		context.lineTo(x, y);
		context.stroke();
		setHasStroke(true);
	}

	function end() {
		if (!drawing.current) {
			return;
		}
		drawing.current = false;
		if (hasStroke) {
			canvasRef.current?.toBlob(blob => onChange(blob), 'image/png');
		} else {
			onChange(null);
		}
	}

	function clear() {
		const canvas = canvasRef.current;
		const context = canvas?.getContext('2d');
		if (!canvas || !context) {
			return;
		}
		context.clearRect(0, 0, canvas.width, canvas.height);
		paintBackground(context, canvas);
		context.strokeStyle = '#111111';
		setHasStroke(false);
		onChange(null);
	}

	useImperativeHandle(ref, () => ({ clear }));

	return (
		<div className="signature-pad">
			<canvas
				ref={canvasRef}
				onPointerDown={start}
				onPointerMove={move}
				onPointerUp={end}
				onPointerCancel={end}
				onPointerLeave={end}
			/>
			<div className="acoes">
				<span>{hasStroke ? 'Assinatura capturada' : 'Assine no campo acima'}</span>
			</div>
		</div>
	);
});

export default SignaturePad;
